import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Kontekst} from "felles/tjenester/Kontekst";
import {AutentisertOkModell, AvtaleModell, BrukerModell, EnkelGruppeModell, GruppeId, GruppeInvitasjonTilGodkjenningModell, GruppeMedlemListeModell, GruppeSomKreverAnnenAutentiseringsmetodeModell, JwtTokenInfoModell, TilgangsrolleBruker, TilgangsrolleMedlem, TilgjengeligGruppeModell} from "./ressurser/generertNg2HttpKlient";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Brukervalg, Featurevalg} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {verifiserErIkkeNil} from "felles-ng2/støttefunksjoner/antakelser";
import {første} from "felles-ng2/støttefunksjoner/lister";

const debug = initDebugger('Brukerkontekst');

/**
 * Informasjon om innlogget bruker.
 * Tilgjengeliggjort via {@link BrukerkontekstTilbyder}.
 *
 * Erstatter ajs sin {@link Kontekst}.
 */
export class Brukerkontekst {

    constructor(private readonly autentisert: AutentisertOkModell) {
        debug("initFraAutentisert - populerer kontekst fra %O", autentisert)
    }

    get bruker(): BrukerModell {
        return this.autentisert.bruker;
    }

    get tilgjengeligeGrupper(): TilgjengeligGruppeModell[] {
        return this.autentisert.tilgjengeligeGrupper;
    }

    get grupperSomKreverAnnenAutentiseringsmetode(): GruppeSomKreverAnnenAutentiseringsmetodeModell[] {
        return this.autentisert.grupperSomKreverAnnenAutentiseringsmetode;
    }

    get invitasjonerTilGodkjenning(): GruppeInvitasjonTilGodkjenningModell[] {
        return this.autentisert.invitasjonerTilGodkjenning;
    }

    get kontaktpersonForAvtaler(): AvtaleModell[] {
        return this.autentisert.kontaktpersonForAvtaler;
    }

    get brukervalg(): Brukervalg {
        return {...Kontekst.standardBrukervalg() as any as Brukervalg, ...this.autentisert.brukervalg as Brukervalg};
    }

    get featurevalg(): Featurevalg {
        return {...Kontekst.standardFeaturevalg(), ...this.autentisert.featurevalg as Featurevalg};
    }

    /** Sann dersom angitt feature er aktivert i brukerens featurevalg, tilsvarer {@link Kontekst#erFeatureAktivert} */
    erFeatureAktivert(featurenavn: string): boolean {
        return this.featurevalg[featurenavn] === true;
    }

    get aktivtTokenInfo(): JwtTokenInfoModell {
        return this.autentisert.jwtTokenInfo;
    }

    /** sann dersom innlogget bruker har et aktivt medlemskap i angitt gruppe og er autentisert med metode som aksepteres av gruppen */
    harTilgangTilGruppe(gruppeId: GruppeId) {
        return erIkkeNil(this.finnAutentiserteGruppe(gruppeId));
    }

    /** sann dersom innlogget bruker har angitt brukerrolle */
    inneharTilgangsrolle(brukerrolle: TilgangsrolleBruker): boolean {
        return this.bruker.tilgangsroller.includes(brukerrolle)
    }

    /** sann dersom innlogget brukers medlemrepresentasjon i angitt gruppe har angitt tilgangsrollerMedlem */
    inneharTilgangsrolleIGruppe(rolle: TilgangsrolleMedlem, gruppeId: GruppeId): boolean {
        if (erNil(gruppeId)) return false;
        const medlem = this.medlemRepresentasjonTilknyttetEllerNull(gruppeId);
        if (erNil(medlem)) return false;
        return medlem.tilgangsroller.includes(rolle);
    }

    /** sann dersom innlogget brukers medlemrepresentasjon i angitt gruppe IKKE har angitt tilgangsrollerMedlem */
    ikkeInneharTilgangsrolleIGruppe(rolle: TilgangsrolleMedlem, gruppeId: GruppeId): boolean {
        return !this.inneharTilgangsrolleIGruppe(rolle, gruppeId);
    }

    /** sann dersom minst en av innlogget brukers gruppemedlemskap har angitt rolle */
    inneharTilgangsrolleINoenGruppe(rolle: TilgangsrolleMedlem): boolean {
         return this.tilgjengeligeGrupper
          .map(gruppe => gruppe.medlemskap)
          .flatMap(medlem => medlem.tilgangsroller)
          .some(tilgangsrolle => tilgangsrolle === rolle);
    }

    /** Innlogget brukers medlemskap i angitt gruppe, null dersom ikke funnet */
    medlemRepresentasjonTilknyttetEllerNull(gruppeId: string): GruppeMedlemListeModell | null {
        return this.tilgjengeligeGrupper
          .find(g => g.gruppe.id === gruppeId)
          ?.medlemskap ?? null;
    }

    /** Innlogget brukers medlemskap i angitt gruppe, kaster exception dersom ikke funnet */
    medlemRepresentasjonTilknyttet(gruppeId: string): GruppeMedlemListeModell {
        return verifiserErIkkeNil(
          this.medlemRepresentasjonTilknyttetEllerNull(gruppeId),
          ()=>`kallende bruker '${this.bruker.brukernavn}(${this.bruker.id})' har ikke tilgang til gruppen '${gruppeId}', bruker har enten ikke noe aktivt medlemskap i denne eller har ikke logget inn med tilstrekkelig innloggingsnivå`);
    }

    /** finn gruppe basert på id som innlogget bruker har et aktivt medlemskap i og er autentisert med metode som aksepteres av gruppen*/
    finnAutentiserteGruppe(gruppeId: GruppeId): EnkelGruppeModell | null {
        return første(this.finnAutentiserteGrupper([gruppeId]));
    }

    /** finn grupper basert på ider som innlogget bruker har et aktivt medlemskap i og er autentisert med metode som aksepteres av gruppen */
    finnAutentiserteGrupper(gruppeIder: GruppeId[]): EnkelGruppeModell[] {
        return this.tilgjengeligeGrupper
          .map(g => g.gruppe)
          .filter(g => gruppeIder.includes(g.id));
    }

    //hjelpemetoder


}
